// src/pages/CollectionOrders/components/CollectionOrderCreate.tsx
import React, { useContext, useEffect, useState } from 'react';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import { AppContext } from '../../../contexts/AppContext';
import { toast } from 'react-toastify';
import useCollectionOrderService from '../../../app/services/hooks/useCollectionOrderService';
import { CollectionOrderDataForm } from '../../../app/models/CollectionOrder';
import ButtonCancelForm from '../../../components/buttons/ButtonCancelForm';
import ButtonSaveForm from '../../../components/buttons/ButtonSaveForm';
import CollectionOrderForm from './forms/CollectionOrderForm';
import { CollectionOrderStorePayload } from '../../../app/models/CollectionOrder';

interface Props {
  onSaved?: () => void;
  onCancel?: () => void;
  onError?: () => void;
}

type SelectOpt = { id: number | string; name: string };
const NOOP = () => undefined;

const CollectionOrderCreate = ({ onSaved, onCancel, onError }: Props) => {
  const { showLoading, hideLoading } = useContext(AppContext);
  const { createCollectionOrder, storeCollectionOrder, fetchingStoreCollectionOrder } = useCollectionOrderService();

  const handleCancel = onCancel ?? NOOP;

  const [order, setOrder] = useState<CollectionOrderDataForm>({
    society_id: null,
    project_id: null,
    program_date: '',
    start_date: null,
    end_date: null,
    status: 'CREATED',
    is_periodic: false,
    recurrence_days: [],
    program_time: '08:00',
    // legacy (no se usan en create):
    batch_id: null,
    quantity: null as any,          // deprecado en create
    unit_id: null as any,           // deprecado en create
    collector_user_id: null,
    is_active: true,
    // planificación real:
    planned_quantity: null as any,
    planned_unit_id: null as any,
  });

  const [societies, setSocieties] = useState<SelectOpt[]>([]);
  const [projects, setProjects] = useState<SelectOpt[]>([]);
  const [units, setUnits] = useState<SelectOpt[]>([]);
  const [statuses, setStatuses] = useState<SelectOpt[]>([]);
  const [errorFields, setErrorFields] = useState<any>();

  useEffect(() => { create(); }, []);

  const create = () => {
    showLoading?.('loading', 'Cargando datos de formulario...');
    createCollectionOrder({
      onSuccess: (response: ServiceResponse) => {
        setSocieties(response.data.societies || []);
        setProjects(response.data.projects || []);
        // ignoramos response.data.batches / collectors en create
        setUnits(response.data.units || []);
        setStatuses(response.data.status || []);
        hideLoading?.();
      },
      onError: (response: ServiceResponse) => {
        hideLoading?.();
        toast.error(response.message);
        onError && onError();
      }
    });
  };

  const store = () => {
    if (!order.society_id) { toast.error('Debe seleccionar una sociedad'); return; }
    if (!order.project_id) { toast.error('Debe seleccionar un proyecto'); return; }
    if (!order.program_date) { toast.error('Debe indicar la fecha de programa'); return; }
    if (order.is_periodic) {
      if (!order.recurrence_days?.length) { toast.error('Debe seleccionar al menos un día de la semana'); return; }
      if (!order.end_date) { toast.error('Debe indicar la fecha hasta la cual crear órdenes'); return; }
      if (order.end_date < order.program_date) { toast.error('La fecha "Hasta el" debe ser igual o posterior a la fecha de inicio'); return; }
    }

    // base requerido (program_date obligatoria; start_date se asigna al pasar a En progreso)
    const base: CollectionOrderStorePayload = {
      society_id: order.society_id as number,
      project_id: order.project_id as number,
      program_date: order.program_date as string,
      ...(order.start_date ? { start_date: order.start_date } : {}),
      ...(order.status ? { status: order.status as any } : {}),
    };

    // regla: planned_* se envían solo si vienen ambos
    const withPlanning: CollectionOrderStorePayload = {
      ...base,
      ...(
        order.planned_quantity != null && order.planned_unit_id != null
          ? {
              planned_quantity: Number(order.planned_quantity),
              planned_unit_id: order.planned_unit_id as number,
            }
          : {}
      ),
    };

    // Orden periódica (solo fecha de inicio y hasta el; hora no se usa por ahora)
    const finalPayload: CollectionOrderStorePayload = order.is_periodic
      ? {
          ...withPlanning,
          is_periodic: true,
          recurrence_days: order.recurrence_days ?? [],
          end_date: order.end_date ?? null,
        }
      : withPlanning;

    showLoading?.('loading', order.is_periodic ? 'Creando órdenes...' : 'Guardando orden...');
    storeCollectionOrder(finalPayload, {
      onSuccess: (response: ServiceResponse) => {
        hideLoading?.();
        const count = response.data?.created_count ?? 1;
        toast.success(response.message || (count > 1 ? `Se crearon ${count} órdenes.` : 'Orden creada.'), { autoClose: 2500 });
        onSaved && onSaved();
      },
      onError: (response: ServiceResponse) => {
        hideLoading?.();
        toast.error(response.message);
      },
      onFieldError: (errs: ServiceResponse) => {
        hideLoading?.();
        setErrorFields(errs.data);
      }
    });
  };


  return (
    <>
      <div className="row">
        <div className="col-12">
          <CollectionOrderForm
            dataForm={order}
            setDataForm={setOrder}
            societies={societies}
            projects={projects}
            units={units}
            statuses={statuses}
            errorFields={errorFields}
          />
        </div>
      </div>
      <hr />
      <div className="row justify-content-end">
        <div className="col-auto">
          <ButtonCancelForm callbackCancel={handleCancel} locked={!!fetchingStoreCollectionOrder} />
        </div>
        <div className="col-auto">
          <ButtonSaveForm callbackSave={store} locked={!!fetchingStoreCollectionOrder} />
        </div>
      </div>
    </>
  );
};

export default CollectionOrderCreate;
